import { Link, Outlet } from 'react-router-dom';
import { CalendarCheck, ChevronRight, Clock, History, MapPin, Navigation } from 'lucide-react';
import type { Intervention } from '@hch/shared';
import { AppShell, type NavItem } from '~/components/layout/app-shell';
import { Button } from '~/components/ui/button';
import {
  Card,
  CardContent,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '~/components/ui/primitives';
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  StatusBadge,
} from '~/components/shared/states';
import { useAgenda } from '~/lib/queries';
import { directionsUrl, formatRelativeDay, formatSlotRange, fullName } from '~/lib/utils';

const ITEMS: NavItem[] = [
  { to: '/technicien', label: 'Mon agenda', icon: CalendarCheck, end: true },
];

export function TechnicianLayout() {
  return (
    <AppShell items={ITEMS} title="Espace technicien">
      <Outlet />
    </AppShell>
  );
}

/**
 * Agenda du technicien.
 *
 * Concu pour le terrain : une seule colonne, des cibles tactiles larges, l'essentiel
 * visible sans defilement (heure, client, adresse), et un bouton d'itineraire qui
 * ouvre directement l'application de navigation du telephone.
 */
export function TechnicianAgendaPage() {
  const { data, isLoading, error, refetch } = useAgenda();

  return (
    <div className="app-page">
      <PageHeader
        title="Mon agenda"
        description={
          data
            ? `${data.today.length} intervention${data.today.length > 1 ? 's' : ''} aujourd'hui.`
            : undefined
        }
      />

      {isLoading ? <LoadingState /> : null}
      {error ? <ErrorState error={error} onRetry={() => void refetch()} /> : null}

      {data ? (
        <Tabs defaultValue="today">
          <TabsList className="w-full sm:w-auto">
            <TabsTrigger value="today">Aujourd'hui ({data.today.length})</TabsTrigger>
            <TabsTrigger value="upcoming">A venir ({data.upcoming.length})</TabsTrigger>
            <TabsTrigger value="past">Passees</TabsTrigger>
          </TabsList>

          <TabsContent value="today" className="pt-4">
            {data.today.length === 0 ? (
              <EmptyState
                icon={CalendarCheck}
                title="Aucune intervention aujourd'hui"
                description="Profitez-en pour preparer votre materiel."
              />
            ) : null}
            <div className="flex flex-col gap-3">
              {data.today.map((item) => (
                <TechnicianCard key={item.id} intervention={item} highlight />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="upcoming" className="pt-4">
            {data.upcoming.length === 0 ? (
              <EmptyState icon={Clock} title="Aucune intervention planifiee" />
            ) : null}
            <div className="flex flex-col gap-3">
              {data.upcoming.map((item) => (
                <TechnicianCard key={item.id} intervention={item} showDate />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="past" className="pt-4">
            {data.past.length === 0 ? (
              <EmptyState icon={History} title="Aucune intervention passee" />
            ) : null}
            <div className="flex flex-col gap-3">
              {data.past.map((item) => (
                <TechnicianCard key={item.id} intervention={item} showDate />
              ))}
            </div>
          </TabsContent>
        </Tabs>
      ) : null}
    </div>
  );
}

function TechnicianCard({
  intervention,
  highlight,
  showDate,
}: {
  intervention: Intervention;
  highlight?: boolean;
  showDate?: boolean;
}) {
  const address = intervention.address;

  return (
    <Card className={highlight ? 'gap-3 border-primary/40 py-4' : 'gap-3 py-4'}>
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-0.5">
            <p className="text-lg font-semibold tabular-nums">
              {formatSlotRange(intervention.startsAt, intervention.endsAt)}
            </p>
            {showDate ? (
              <p className="text-xs capitalize text-muted-foreground">
                {formatRelativeDay(intervention.startsAt)}
              </p>
            ) : null}
          </div>
          <StatusBadge status={intervention.status} />
        </div>

        <div className="space-y-1 text-sm">
          <p className="font-medium">{fullName(intervention.client)}</p>
          <p className="text-muted-foreground">{intervention.packageName}</p>
          {address ? (
            <p className="flex items-start gap-1.5 text-muted-foreground">
              <MapPin className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span>
                {address.street}, {address.postalCode} {address.city}
              </span>
            </p>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button asChild size="touch" className="flex-1">
            <Link to={`/technicien/interventions/${intervention.id}`}>
              Ouvrir le dossier
              <ChevronRight aria-hidden />
            </Link>
          </Button>

          {address ? (
            <Button asChild size="touch" variant="outline">
              <a
                href={directionsUrl(address.lat, address.lng)}
                target="_blank"
                rel="noreferrer"
                aria-label={`Itineraire vers ${address.street}`}
              >
                <Navigation aria-hidden />
                Itineraire
              </a>
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
